import React from 'react';
import { DatePicker, DatePickerProps } from 'antd';
import moment, { Moment } from 'moment';
import dayjs, { Dayjs } from 'dayjs';
import './CustomDatePicker.css';

// Configure moment to use consistent date format
moment.defaultFormat = 'DD-MMM-YYYY';

// Helper to convert moment to dayjs
// Uses local date components to avoid timezone conversion issues
const momentToDayjs = (momentDate: Moment | null): Dayjs | null => {
  if (!momentDate || !momentDate.isValid()) return null;
  // Use local date components to avoid timezone shifts
  // dayjs doesn't accept array like moment, so we create a Date object at midnight local time
  const year = momentDate.year();
  const month = momentDate.month(); // Already 0-indexed
  const day = momentDate.date();
  // Create a Date object at midnight local time
  const localDate = new Date(year, month, day, 0, 0, 0, 0);
  return dayjs(localDate);
};

/**
 * Helper function to parse dates from backend (handles multiple formats)
 * Supports: ISO strings, YYYY-MM-DD, DD-MMM-YYYY, timestamps, etc.
 * IMPORTANT: Parses dates as local dates to avoid timezone shifts
 */
export const parseDate = (dateValue: any): Moment | null => {
  if (!dateValue) return null;

  // If it's already a moment object, validate and return
  if (moment.isMoment(dateValue)) {
    return dateValue.isValid() ? dateValue.startOf('day') : null;
  }

  // If it's a string in YYYY-MM-DD format (most common from backend)
  // Parse it as a local date to avoid UTC conversion issues
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    // Parse as local date (not UTC) to prevent day shifts
    const parts = dateValue.split('-');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // moment months are 0-indexed
    const day = parseInt(parts[2], 10);
    const parsed = moment([year, month, day]);
    return parsed.isValid() ? parsed.startOf('day') : null;
  }

  // Try parsing with common formats (strict mode to avoid timezone issues)
  const formats = [
    'YYYY-MM-DD',
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
    'YYYY-MM-DDTHH:mm:ssZ',
    'YYYY-MM-DD HH:mm:ss',
    'DD-MMM-YYYY',
    'DD/MM/YYYY',
    'MM/DD/YYYY',
    moment.ISO_8601
  ];

  for (const format of formats) {
    const parsed = moment(dateValue, format, true);
    if (parsed.isValid()) {
      // If format includes timezone (Z), parse as UTC then convert to local
      // Otherwise, parse as local date
      const formatStr = typeof format === 'string' ? format : String(format);
      if (formatStr.includes('Z') || format === moment.ISO_8601) {
        return parsed.utc().local().startOf('day');
      }
      return parsed.startOf('day');
    }
  }

  // Try default parsing (as last resort)
  const defaultParsed = moment(dateValue);
  return defaultParsed.isValid() ? defaultParsed.startOf('day') : null;
};

/**
 * Helper function to format date for backend (YYYY-MM-DD format)
 * Uses local time to avoid timezone issues that cause date shifts
 * IMPORTANT: Always uses local date components, never UTC
 */
export const formatDateForBackend = (dateValue: any): string | null => {
  if (!dateValue) return null;

  // If it's already a string in YYYY-MM-DD format, return as-is
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }

  // If it's a moment object, format it
  if (moment.isMoment(dateValue) && dateValue.isValid()) {
    // Ensure we're in local mode (not UTC) before extracting date components
    const localMoment = dateValue.local();
    // Use local time to avoid UTC conversion issues
    // Format as YYYY-MM-DD using local date components
    const year = localMoment.year();
    const month = localMoment.month() + 1; // moment months are 0-indexed
    const day = localMoment.date(); // Gets local date (not UTC date)
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  // If it's an ISO datetime string, extract just the date part
  if (typeof dateValue === 'string' && dateValue.includes('T')) {
    const datePart = dateValue.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }
  }

  // Try to parse and reformat
  const parsed = parseDate(dateValue);
  if (parsed && parsed.isValid()) {
    // Ensure we're in local mode before extracting
    const localMoment = parsed.local();
    const year = localMoment.year();
    const month = localMoment.month() + 1;
    const day = localMoment.date();
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  return null;
};

export interface CustomDatePickerProps extends Omit<DatePickerProps, 'value' | 'onChange'> {
  /**
   * Value can be a moment object, date string, or null
   * Will be automatically parsed
   */
  value?: Moment | string | null;

  /**
   * onChange callback receives a moment object or null
   * The moment object is normalized to start of day
   */
  onChange?: (date: Moment | null) => void;

  /**
   * If true, formats the onChange value as YYYY-MM-DD string for backend
   * Default: false (returns moment object)
   */
  formatForBackend?: boolean;

  /**
   * Display format for the date picker
   * Default: 'DD-MM-YYYY hh:mm A'
   */
  displayFormat?: string;

  /**
   * Whether to show time picker
   * Default: true
   */
  showTime?: boolean | object;
}

/**
 * Custom DatePicker component that handles all date-related issues:
 * - Proper date parsing from backend (multiple formats)
 * - Proper date formatting for backend
 * - Fixed calendar display issues
 * - Consistent moment.js handling
 * - Proper date normalization
 */
const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  formatForBackend = false,
  displayFormat = 'DD-MM-YYYY hh:mm A',
  format = displayFormat,
  getPopupContainer,
  showToday = true,
  allowClear = false,
  showTime = true,
  className = '',
  ...restProps
}) => {
  // Parse the value to moment object, then convert to dayjs for Ant Design
  const parsedValue = React.useMemo(() => {
    const momentValue = parseDate(value);
    return momentToDayjs(momentValue);
  }, [value]);

  // Handle date change - convert from dayjs back to moment
  const handleChange = (dayjsDate: Dayjs | null, dateString: string | string[]) => {
    if (!onChange) return;

    if (!dayjsDate || !dayjsDate.isValid()) {
      onChange(null);
      return;
    }

    // Convert dayjs to moment
    // For date-time, we should maintain the time component
    const momentDate = moment(dayjsDate.toDate());

    onChange(momentDate);
  };

  // Default popup container
  const defaultGetPopupContainer = (trigger: HTMLElement): HTMLElement => {
    return trigger.parentElement || document.body;
  };

  return (
    <DatePicker
      {...restProps}
      value={parsedValue}
      onChange={handleChange}
      format={format}
      showTime={showTime}
      getPopupContainer={getPopupContainer || defaultGetPopupContainer}
      showToday={showToday}
      allowClear={allowClear}
      className={`custom-date-picker ${className}`}
    />
  );
};

export default CustomDatePicker;

